import { AccountPage } from "./account/Account";
import { SecurityDataPage, StatusPage } from "./account/SecurityData";
import { Approvals, RolesPage } from "./access/Roles";
import { FixedAssets } from "./assets/Assets";
import { Budgets } from "./reports/Budgets";
import { CostCentres } from "./masters/CostCentres";
import { PeoplePage, PersonPage } from "./masters/People";
import { Ask } from "./reports/Ask";
import { Projects, Timesheet } from "./projects/Projects";
import { Developers } from "./company/Developers";
import { PayrollLink } from "./company/PayrollLink";
import { EditLog } from "./company/EditLog";
import { StockTransfers } from "./inventory/StockTransfers";
import { Schemes } from "./parties/Schemes";
import { ExpenseClaimsPage } from "./claims/ExpenseClaims";
import { CashForecast } from "./reports/CashForecast";
import { ScheduledReports } from "./reports/Schedules";
import { GstAnnual } from "./gst/GstAnnual";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { SessionProvider, useSession } from "./auth/session";
import { MfaChallenge, SignIn } from "./auth/SignIn";
import { accountsLink, usesAccounts } from "./lib/supabase";
import { CompanyPicker } from "./companies/CompanyPicker";
import { lastCompany } from "./companies/useMe";
import { Shell } from "./shell/Shell";
import { SectionHub } from "./shell/Section";
import { toUrl } from "./shell/routes";
import { Today } from "./shell/Today";
import { Placeholder } from "./pages/Placeholder";
import { Groups } from "./masters/Groups";
import { Ledgers } from "./masters/Ledgers";
import { LedgerForm } from "./masters/LedgerForm";
import { Openings } from "./masters/Openings";
import { Users } from "./company/Users";
import { Settings } from "./company/Settings";
import { ReportPage } from "./reports/ReportPage";
import { GstReturns } from "./gst/GstReturns";
import { GstChallans } from "./gst/GstPayment";
import { IsdPage } from "./gst/Isd";
import { Challans, Statements, Certificates, Form168 } from "./tds/TdsPages";
import { BankAccounts, Reconcile } from "./banking/Banking";
import { Cheques } from "./banking/Cheques";
import { ChequePrint } from "./banking/ChequePrint";
import { Items, ItemForm, InventorySetup, StockEntry } from "./inventory/Inventory";
import { Import } from "./company/Import";
import { FirmConsole } from "./firms/FirmConsole";
import { DayBook } from "./vouchers/DayBook";
import { VoucherPage } from "./vouchers/VoucherEntry";
import { OrderList, OrderPage } from "./orders/Orders";
import { PartiesPage, PartyPage } from "./parties/Parties";
import { DocumentsSettings } from "./company/DocumentsSettings";
import { PriceLists } from "./parties/PriceLists";
import { Services } from "./parties/Services";
import { PaymentsSettings } from "./company/PaymentsSettings";
import { Portal } from "./portal/Portal";
import { Inbox, PortalSettingsPage } from "./portal/PortalAdmin";
import { BillsOfEntry } from "./purchases/BillsOfEntry";
import { RecurringList, RecurringPage } from "./recurring/Recurring";
import { EwayLimits, EwayRegister } from "./gst/EwayPages";
import { InvoicePrintPage } from "./vouchers/InvoicePrint";
import { LegalPage } from "./legal/Legal";
import { QuickExpense } from "./vouchers/QuickExpense";
import { Composition } from "./gst/Composition";
import { Gstr1aPage, IffPage, RcmDocuments } from "./gst/Gstr1Extras";
import { Ecommerce } from "./gst/Ecommerce";
import { Currencies, Revaluation } from "./masters/Currencies";
import { EmailSettings, MailCallback, OrderPrintPage, PublicDoc, SentMessages, StatementPage } from "./messages/MessagePages";

function Gate() {
  const { session, loading, needsMfa } = useSession();
  if (loading) return <main className="center muted">Loading…</main>;
  // on Altekore's domains signing in happens in Altekore Accounts, which sends the person back here afterwards
  if (!session) { if (usesAccounts()) { location.replace(accountsLink("/signin", location.origin + "/")); return <main className="center muted">Taking you to sign in…</main>; } return <SignIn />; }
  if (needsMfa) { if (usesAccounts()) { location.replace(accountsLink("/two-step", location.origin + "/")); return <main className="center muted">Taking you to two-step sign-in…</main>; } return <MfaChallenge />; }
  return (
    <Routes>
      <Route path="/companies" element={<CompanyPicker />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/firms/:firmId" element={<FirmConsole />} />
      <Route path="/c/:companyId/*" element={<Legacy />} />
      <Route path="/" element={<Shell />}>
        <Route index element={<Today />} />
        <Route path=":sectionId" element={<SectionHub />} />
        <Route path="sales/invoices/new" element={<VoucherPage base="sales" />} />
        <Route path="sales/credit-notes/new" element={<VoucherPage base="credit_note" />} />
        <Route path="sales/receipts/new" element={<VoucherPage base="receipt" />} />
        <Route path="sales/cash-sale" element={<VoucherPage key="cs" base="sales" cash />} />
        <Route path="purchases/cash-purchase" element={<VoucherPage key="cp" base="purchase" cash />} />
        <Route path="purchases/expense" element={<QuickExpense />} />
        <Route path="purchases/bills/new" element={<VoucherPage base="purchase" />} />
        <Route path="purchases/debit-notes/new" element={<VoucherPage base="debit_note" />} />
        <Route path="purchases/payments/new" element={<VoucherPage base="payment" />} />
        <Route path="sales/customers" element={<PartiesPage key="c" side="customer" />} />
        <Route path="sales/customers/:partyId" element={<PartyPage key="c1" side="customer" />} />
        <Route path="purchases/vendors" element={<PartiesPage key="v" side="supplier" />} />
        <Route path="purchases/vendors/:partyId" element={<PartyPage key="v1" side="supplier" />} />
        <Route path="sales/orders" element={<OrderList key="sales" kind="sales" />} />
        <Route path="sales/price-lists" element={<PriceLists />} />
        <Route path="sales/products-services" element={<Services />} />
        <Route path="sales/schemes" element={<Schemes />} />
        <Route path="sales/quotes" element={<OrderList key="quote" kind="quote" />} />
        <Route path="sales/quotes/:orderId" element={<OrderPage key="quote" kind="quote" />} />
        <Route path="sales/quotes/:orderId/print" element={<OrderPrintPage back="orders/quote" />} />
        <Route path="sales/proformas" element={<OrderList key="proforma" kind="proforma" />} />
        <Route path="sales/proformas/:orderId" element={<OrderPage key="proforma" kind="proforma" />} />
        <Route path="sales/proformas/:orderId/print" element={<OrderPrintPage back="orders/proforma" />} />
        <Route path="sales/challans" element={<OrderList key="challan" kind="challan" />} />
        <Route path="sales/challans/:orderId" element={<OrderPage key="challan" kind="challan" />} />
        <Route path="sales/challans/:orderId/print" element={<OrderPrintPage back="orders/challan" />} />
        <Route path="sales/orders/:orderId" element={<OrderPage key="sales" kind="sales" />} />
        <Route path="purchases/orders" element={<OrderList key="purchase" kind="purchase" />} />
        <Route path="purchases/orders/:orderId" element={<OrderPage key="purchase" kind="purchase" />} />
        <Route path="sales/recurring" element={<RecurringList key="sales" kind="sales" />} />
        <Route path="sales/recurring/:recurringId" element={<RecurringPage key="sales" kind="sales" />} />
        <Route path="purchases/recurring" element={<RecurringList key="purchase" kind="purchase" />} />
        <Route path="purchases/recurring/:recurringId" element={<RecurringPage key="purchase" kind="purchase" />} />
        <Route path="banking/transfers/new" element={<VoucherPage base="contra" />} />
        <Route path="accounting/journal/new" element={<VoucherPage base="journal" />} />
        <Route path="transactions/:voucherId" element={<VoucherPage />} />
        <Route path="transactions/:voucherId/print" element={<InvoicePrintPage />} />
        <Route path="accounting/chart-of-accounts" element={<Groups />} />
        <Route path="accounting/accounts" element={<Ledgers />} />
        <Route path="accounting/accounts/:ledgerId" element={<LedgerForm />} />
        <Route path="accounting/opening-balances" element={<Openings />} />
        <Route path="accounting/register" element={<DayBook />} />
        <Route path="sales/invoices" element={<DayBook key="si" base="sales" />} />
        <Route path="sales/payments-received" element={<DayBook key="rc" base="receipt" />} />
        <Route path="sales/credit-notes" element={<DayBook key="cn" base="credit_note" />} />
        <Route path="purchases/bills" element={<DayBook key="pb" base="purchase" />} />
        <Route path="purchases/payments-made" element={<DayBook key="py" base="payment" />} />
        <Route path="purchases/debit-notes" element={<DayBook key="dn" base="debit_note" />} />
        <Route path="accounting/currencies" element={<Currencies />} />
        <Route path="accounting/revaluation" element={<Revaluation />} />
        <Route path="settings/company" element={<Settings />} />
        <Route path="settings/people" element={<Users />} />
        <Route path="settings/invoice-look" element={<DocumentsSettings />} />
        <Route path="settings/email" element={<EmailSettings />} />
        <Route path="settings/portal" element={<PortalSettingsPage />} />
        <Route path="settings/payments" element={<PaymentsSettings />} />
        <Route path="purchases/inbox" element={<Inbox />} />
        <Route path="purchases/bills-of-entry" element={<BillsOfEntry />} />
        <Route path="settings/email/callback" element={<MailCallback />} />
        <Route path="sales/messages" element={<SentMessages />} />
        <Route path="sales/customers/:partyId/statement" element={<StatementPage side="customer" />} />
        <Route path="purchases/vendors/:partyId/statement" element={<StatementPage side="supplier" />} />
        <Route path="sales/orders/:orderId/print" element={<OrderPrintPage back="orders/sales" />} />
        <Route path="purchases/orders/:orderId/print" element={<OrderPrintPage back="orders/purchase" />} />
        <Route path="settings/import" element={<Import />} />
        <Route path="compliance/gstr-1" element={<GstReturns tab="gstr1" />} />
        <Route path="compliance/gstr-2b" element={<GstReturns tab="gstr2b" />} />
        <Route path="compliance/gstr-3b" element={<GstReturns tab="gstr3b" />} />
        <Route path="compliance/e-way-bills" element={<EwayRegister />} />
        <Route path="compliance/composition" element={<Composition />} />
        <Route path="compliance/iff" element={<IffPage />} />
        <Route path="compliance/gstr-1a" element={<Gstr1aPage />} />
        <Route path="compliance/reverse-charge-documents" element={<RcmDocuments />} />
        <Route path="compliance/gstr-9" element={<GstAnnual />} />
        <Route path="compliance/gst-challans" element={<GstChallans />} />
        <Route path="compliance/isd" element={<IsdPage />} />
        <Route path="reports/by-email" element={<ScheduledReports />} />
        <Route path="accounting/fixed-assets" element={<FixedAssets />} />
        <Route path="accounting/budgets" element={<Budgets />} />
        <Route path="accounting/cost-centres" element={<CostCentres />} />
        <Route path="accounting/employees" element={<PeoplePage key="e" kind="employee" />} />
        <Route path="accounting/employees/:personId" element={<PersonPage key="e1" kind="employee" />} />
        <Route path="accounting/students" element={<PeoplePage key="s" kind="student" />} />
        <Route path="accounting/students/:personId" element={<PersonPage key="s1" kind="student" />} />
        <Route path="reports/ask" element={<Ask />} />
        <Route path="sales/projects" element={<Projects />} />
        <Route path="sales/timesheet" element={<Timesheet />} />
        <Route path="settings/api" element={<Developers />} />
        <Route path="settings/payroll" element={<PayrollLink />} />
        <Route path="settings/edit-log" element={<EditLog />} />
        <Route path="purchases/expense-claims" element={<ExpenseClaimsPage />} />
        <Route path="reports/cash-forecast" element={<CashForecast />} />
        <Route path="settings/roles" element={<RolesPage />} />
        <Route path="settings/security" element={<SecurityDataPage />} />
        <Route path="accounting/approvals" element={<Approvals />} />
        <Route path="compliance/e-commerce" element={<Ecommerce />} />
        <Route path="compliance/e-way-bill-limits" element={<EwayLimits />} />
        <Route path="compliance/tds-deposits" element={<Challans />} />
        <Route path="compliance/tds-statements" element={<Statements />} />
        <Route path="compliance/tds-certificates" element={<Certificates />} />
        <Route path="compliance/form-168" element={<Form168 />} />
        <Route path="banking/accounts" element={<BankAccounts />} />
        <Route path="banking/cheques" element={<Cheques />} />
        <Route path="banking/cheques/print/:id" element={<ChequePrint />} />
        <Route path="banking/reconcile" element={<Reconcile />} />
        <Route path="inventory/items" element={<Items />} />
        <Route path="inventory/items/:itemId" element={<ItemForm />} />
        <Route path="inventory/setup" element={<InventorySetup />} />
        <Route path="inventory/stock-adjustment" element={<StockEntry />} />
        <Route path="inventory/transfers" element={<StockTransfers />} />
        <Route path=":area/reports/:slug" element={<ReportPage />} />
        <Route path="reports/:slug" element={<ReportPage />} />
        <Route path="*" element={<Placeholder />} />
      </Route>
    </Routes>
  );
}

// an address from before addresses were readable (/c/<company>/<page>): the company becomes this tab's, then the page
function Legacy() {
  const { companyId, "*": rest } = useParams();
  const loc = useLocation();
  if (companyId) lastCompany.set(companyId);
  return <Navigate to={toUrl((rest ?? "") + loc.search)} replace />;
}

// links sent to customers and vendors open without signing in
function Root() {
  return (
    <Routes>
      <Route path="/d/:token" element={<PublicDoc />} />
      <Route path="/p/:token" element={<Portal />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="*" element={<SessionProvider><Gate /></SessionProvider>} />
    </Routes>
  );
}

export function App() {
  return <BrowserRouter><Root /></BrowserRouter>;
}
